import useSWR from "swr";
import * as aiccLanguageAPI from "services/aiccLanguage";
import { AICC_SWR_CONFIG } from "./swrConfig";

/**
 * The key one language's readable copies live under, exported so a mutation can seed it: after any
 * action the row is re-read in full, and that response already contains the copies this hook would
 * otherwise go and ask for again.
 */
export function aiccLanguageContentVersionsKey(languageId: number) {
  return ["aicc/language-content-versions", languageId];
}

/**
 * The readable copies of one language's content — what a version row's sub-asset table is drawn
 * from.
 *
 * Deliberately per language and not part of the list: computing the fourteen asset breakdowns walks
 * the content partitions and costs the backend ~1.4s per language, which the list paid on every row
 * for a screen where every card starts collapsed. So the list is read with `?assets=false` and this
 * runs when a card is opened.
 *
 * Passing no id keeps SWR from requesting anything, which is how a caller that has the copies
 * already avoids the round trip.
 */
export function useAiccLanguageContentVersions(languageId?: number) {
  const { data, error, isLoading, mutate } = useSWR(
    languageId ? aiccLanguageContentVersionsKey(languageId) : null,
    () => aiccLanguageAPI.fetchAiccLanguageContentVersions(languageId as number),
    AICC_SWR_CONFIG
  );

  return {
    contentVersions: data ?? [],
    /**
     * True only while there is nothing to show. Callers must branch on this before looking at the
     * copies: a language with none is a real answer, and an empty list is what both look like.
     */
    isLoading,
    isError: !!error,
    mutate,
  };
}
